import { useState } from "react";
import { Button } from "@headlessui/react";
import { PencilIcon } from "@heroicons/react/24/solid";
import { ChevronDownIcon, ChevronRightIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { handleEnterSpaceKey } from "../../../app/keyboardUtils";
import {
	isAdminRole,
	isHigherThanDistrictAdmin,
	setHasImage,
	UserRole,
} from "../../../app/userSlice";
import Avatar from "../../../components/Avatar";
import ButtonWithConfirm from "../../../components/ButtonWithConfirm";
import ModalFrame from "../../../components/ModalFrame";
import FormattedDate from "../../../components/FormattedDate";
import { ClassLinkOrCleverIconWithTooltip } from "../../../components/ClassLinkOrCleverIcon";
import { useRemoveAvatarMutation } from "../../account/accountApiSlice";
import { useGetGradesQuery } from "../../lookup/lookupApiSlice";
import { EditUserViewModel, Gender, Ethnicity } from "../usersApiSlice";
import EditUserInformationForm from "./EditUserInformationForm";
import { externalIdLabel } from "../usersUtil";
import UserOutsideSiteSettingsForm from "./UserOutsideSiteSettingsForm";

interface ProfileProps {
	userInfo: EditUserViewModel;
	setSuccessMessage: (message: string) => void;
	setErrorMessage: (message: string) => void;
}

export default function UserProfileSection(props: ProfileProps) {
	const { userId } = useAppSelector((state) => state.user);
	const { config } = useAppSelector((state) => state.site);

	const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
	const [isOutsideSettingsFormOpen, setIsOutsideSettingsFormOpen] = useState(false);
	const [showDemographicSection, setShowDemographicSection] = useState(false);

	const showOutsideSite =
		!config?.isUtahComposeSite &&
		(props.userInfo.showCleverID || props.userInfo.showClassLinkID);
	const outsideSiteId = props.userInfo.showCleverID
		? (props.userInfo.cleverID ?? "")
		: (props.userInfo.classLinkID ?? "");

	const [removeUserImage] = useRemoveAvatarMutation();

	const gradesResult = useGetGradesQuery();
	const grades = gradesResult.data;

	const dispatch = useAppDispatch();

	const handleOutsideSettingsFormClose = () => setIsOutsideSettingsFormOpen(false);

	function handleRemoveUserImage() {
		props.setErrorMessage("");
		var mutation =
			props.userInfo.id === userId
				? removeUserImage()
				: removeUserImage({ userId: props.userInfo.id });

		mutation
			.unwrap()
			.then(() => {
				if (props.userInfo.id === userId) dispatch(setHasImage(false));
			})
			.catch((error) => {
				props.setErrorMessage(error.data.message);
				scrollTo(0, 0);
			});
	}

	function toggleShowDemographicSection() {
		setShowDemographicSection(!showDemographicSection);
	}

	return (
		<section className="w-full pl-6 self-start">
			<div className="flex h-[70px] items-center justify-between gap-x-4 pr-5 py-4">
				<p className="text-xl font-medium">User Profile</p>
				<Button
					id="edit-user-info-button"
					className="btn btn-primary"
					onClick={() => setIsEditUserDialogOpen(true)}>
					<span className="flex items-center">
						<PencilIcon className="size-4 mr-2" /> Edit User Profile
					</span>
				</Button>
			</div>
			<div className="pr-5">
				<div className="flex gap-x-2 items-start">
					<div className="mt-2 flex items-center relative">
						<Avatar
							className="h-14 w-14 block"
							hasImage={props.userInfo.hasImage}
							initials={props.userInfo.initials}
							userId={props.userInfo.id}
						/>
						{props.userInfo.hasImage && props.userInfo.canRemoveUserImage ? (
							<ButtonWithConfirm
								id="remove-avatar-button"
								className="absolute -bottom-2 -right-2 hover:cursor-pointer"
								message={`Do you want to remove the avatar for ${props.userInfo.fullName}?`}
								confirmLabel="Yes"
								cancelLabel="No"
								onConfirm={() => handleRemoveUserImage()}>
								<XCircleIcon className="mx-auto size-8 fill-red-700 stroke-white" />
							</ButtonWithConfirm>
						) : null}
					</div>
					<div
						className={`${props.userInfo.role === UserRole.Writer ? "flex gap-x-4" : ""} w-full`}>
						<div
							className={`${props.userInfo.role === UserRole.Writer ? "w-3/5" : ""} grid grid-cols-[max-content_minmax(0,1fr)] gap-4 self-start pl-3 pt-2 pb-4`}>
							<div className="font-medium text-gray-600">Name</div>
							<div className="break-words">{props.userInfo.fullName}</div>

							{isHigherThanDistrictAdmin(props.userInfo.editorsRole) && (
								<>
									<div className="font-medium text-gray-600">Username</div>
									<div className="break-all">
										{props.userInfo.editModel.userName}
									</div>
								</>
							)}

							{!isAdminRole(props.userInfo.role) && (
								<>
									<div className="font-medium text-gray-600">
										{externalIdLabel(props.userInfo.role, config)}
									</div>
									<div className="break-all">
										{props.userInfo.editModel.externalID}
									</div>
								</>
							)}

							{props.userInfo.editorsRole === UserRole.Admin && showOutsideSite && (
								<>
									<div className="font-medium text-gray-600">
										{props.userInfo.showCleverID ? "Clever" : "ClassLink"} ID
									</div>
									<div className="relative min-h-6 overflow-visible whitespace-nowrap">
										<span className={`${outsideSiteId ? "mr-3" : ""}`}>
											{outsideSiteId}
										</span>
										<Button
											id="outside-site-form-button"
											className="cursor-pointer inline-flex justify-center overflow-visible absolute top-1/2 -translate-y-1/2"
											onClick={() => setIsOutsideSettingsFormOpen(true)}>
											<ClassLinkOrCleverIconWithTooltip
												iconClasses="size-7 !rounded-md"
												isClassLinkEntity={props.userInfo.showClassLinkID}
												isCleverEntity={props.userInfo.showCleverID}
												tooltipId="edit-user-outside-site-settings"
												tooltipContent={`Edit ${props.userInfo.showCleverID ? "Clever" : "ClassLink"} Settings`}
											/>
										</Button>
									</div>
									{isOutsideSettingsFormOpen && (
										<ModalFrame
											open={isOutsideSettingsFormOpen}
											title={`${props.userInfo.showCleverID ? "Clever Settings" : "ClassLink Settings"}`}
											onClose={handleOutsideSettingsFormClose}>
											<UserOutsideSiteSettingsForm
												userId={props.userInfo.id}
												isCleverForm={props.userInfo.showCleverID}
												onClose={handleOutsideSettingsFormClose}
											/>
										</ModalFrame>
									)}
								</>
							)}

							{props.userInfo.role !== UserRole.Writer && (
								<>
									<div className="font-medium text-gray-600">Email</div>
									<div className="break-all">
										{props.userInfo.editModel.emailAddress}
									</div>
									<div className="font-medium text-gray-600">Phone</div>
									<div>{props.userInfo.editModel.phone}</div>
								</>
							)}
						</div>
						{props.userInfo.role === UserRole.Writer && (
							<div className="w-2/5 shrink-0 grid grid-cols-[minmax(0,_max-content)_1fr] gap-4 self-start pt-2 pl-3 pb-4">
								<div className="font-medium text-gray-600">Date of Birth</div>
								<div>
									{props.userInfo.editModel.dateOfBirth && (
										<FormattedDate
											date={props.userInfo.editModel.dateOfBirth}
										/>
									)}
								</div>
								<div className="font-medium text-gray-600">Grade</div>
								<div>
									{props.userInfo.editModel.gradeId
										? (grades?.find(
												(g) => g.gradeId == props.userInfo.editModel.gradeId
											)?.shortName ?? "")
										: ""}
								</div>
							</div>
						)}
					</div>
				</div>
				{props.userInfo.role === UserRole.Writer &&
					(!config?.isUtahComposeSite ||
						(config?.isUtahComposeSite &&
							isHigherThanDistrictAdmin(props.userInfo.editorsRole))) && (
						<section className="pl-14 pb-4">
							<div className="flex gap-x-2 items-center pl-5">
								<div className="flex items-center">
									{showDemographicSection ? (
										<ChevronDownIcon
											tabIndex={0}
											className="size-5 hover:cursor-pointer"
											onClick={toggleShowDemographicSection}
											onKeyDown={(e) =>
												handleEnterSpaceKey(e, toggleShowDemographicSection)
											}
										/>
									) : (
										<ChevronRightIcon
											tabIndex={0}
											className="size-5 hover:cursor-pointer"
											onClick={toggleShowDemographicSection}
											onKeyDown={(e) =>
												handleEnterSpaceKey(e, toggleShowDemographicSection)
											}
										/>
									)}
									<h3 className="text-base font-semibold text-gray-900 ml-4">
										Demographic Information
									</h3>
								</div>
							</div>
							{showDemographicSection && (
								<div className="grid grid-cols-2 gap-x-2">
									<div className="grid grid-cols-[max-content_1fr] gap-x-8 gap-y-4 items-baseline self-start pl-5 pt-4">
										<div className="font-medium text-gray-600">Ethnicity</div>
										<div>
											{props.userInfo.editModel.ethnicity
												? Ethnicity[
														props.userInfo.editModel
															.ethnicity as keyof typeof Ethnicity
													]
												: "--"}
										</div>
										<div className="font-medium text-gray-600">Gender</div>
										<div>
											{props.userInfo.editModel.gender
												? Gender[
														props.userInfo.editModel
															.gender as keyof typeof Gender
													]
												: "--"}
										</div>
										<div className="font-medium text-gray-600">Disability</div>
										<div>
											{props.userInfo.editModel.disability
												? props.userInfo.editModel.disability
												: "--"}
										</div>
									</div>
									<div className="grid grid-cols-[minmax(0,_max-content)_1fr] gap-x-8 gap-y-4 items-baseline self-start pl-3 mr-5 pt-4">
										<div className="font-medium text-gray-600">
											Economically Disadvantaged
										</div>
										<div className="whitespace-nowrap">
											{props.userInfo.editModel.economicallyDisadvantaged ==
											null
												? "--"
												: props.userInfo.editModel.economicallyDisadvantaged
													? "Yes"
													: "No"}
										</div>
										<div className="font-medium text-gray-600">
											Limited English
										</div>
										<div>
											{props.userInfo.editModel.limitedEnglish == null
												? "--"
												: props.userInfo.editModel.limitedEnglish
													? "Yes"
													: "No"}
										</div>
									</div>
								</div>
							)}
						</section>
					)}
			</div>
			{isEditUserDialogOpen && (
				<ModalFrame
					open={isEditUserDialogOpen}
					title={`Edit User: ${props.userInfo.fullName}`}
					overflow="overflow-visible"
					onClose={() => setIsEditUserDialogOpen(false)}>
					<EditUserInformationForm
						userInfo={props.userInfo}
						onClose={() => setIsEditUserDialogOpen(false)}
					/>
				</ModalFrame>
			)}
		</section>
	);
}
